import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { sortBy } from "lodash";
import { useMutation } from "@apollo/client";
import { CreateLLMSession } from "gql/mutations/llm.graphql";
import { checkPermission } from "@/admin/utils";

// Define types for our context
interface AIMessage {
  status: "streaming" | "complete" | "error"
  chunk?: {
    text: string
  }
  error?: string
  message?: string
  request_type?: string
  result?: any
  timestamp: number
  sequence: number
}

// Define request queue item
interface QueuedRequest {
  requestType: string
  params: any
}

interface AIManagerContextType {
  isProcessing: boolean
  messages: AIMessage[]
  completedMessages: AIMessage[]
  sendAIRequest: (requestType: string, params: any) => void
  clearMessages: () => void
  totalClearMessages: () => void
  hasAiPermission: boolean
}

// Create a context with default values
export const AIManagerContext = React.createContext<AIManagerContextType>({
  isProcessing: false,
  messages: [],
  completedMessages: [],
  sendAIRequest: () => { },
  clearMessages: () => { },
  totalClearMessages: () => { },
  hasAiPermission: false
});

// Export a named Provider component for simpler usage
export const AIManagerContextProvider = ({ children }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [completedMessages, setCompletedMessages] = useState<AIMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionUrl, setSessionUrl] = useState<string | null>(null);
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [hasAiPermission, setHasAiPermission] = useState(false);

  useEffect(() => {
    checkPermission(["ai_unlimited", "ai_limited"]).then((hasPermission) => {
      setHasAiPermission(hasPermission as boolean);
    });
  }, [true]);

  // Use useMutation hook for LLM session
  const [createLLMSession, { loading: sessionLoading }] = useMutation(CreateLLMSession, {
    onCompleted: (data) => {
      if (data?.llm_session) {
        setSessionId(data.llm_session.session_id);
        setSessionUrl(data.llm_session.url);
      }
    },
    onError: (error) => {
      console.error("Error creating LLM session:", error);
      // Add error message
      const errorMessage: AIMessage = {
        status: "error",
        error: error.message,
        timestamp: Date.now(),
        sequence: messages.length
      };
      setMessages(prevMessages => [...prevMessages, errorMessage]);
    }
  });

  useEffect(() => {
    if (sessionLoading) {
      setIsProcessing(true);
    }
  }, [sessionLoading]);

  // Queue to hold requests before session is established
  const requestQueueRef = useRef<QueuedRequest[]>([]);

  // Initialize WebSocket connection when sessionId and URL are available
  useEffect(() => {
    if (!sessionId || !sessionUrl) return;

    const socket = new WebSocket(sessionUrl, sessionId);
    socket.onopen = () => {
      console.log("WebSocket connected to LLM service");

      // Process any queued requests
      if (requestQueueRef.current.length > 0) {
        const queuedRequests = [...requestQueueRef.current];
        requestQueueRef.current = []; // Clear the queue

        // Process each queued request
        queuedRequests.forEach(request => {
          sendRequestToWebSocket(socket, request.requestType, request.params);
        });
      }
    };

    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // Add timestamp to incoming message
        const message: AIMessage = {
          ...data,
          timestamp: Date.now(),
          sequence: data.sequence || messages.length
        };

        // Add to the stream of messages
        setMessages(prevMessages => {
          const existingMessageIndex = prevMessages.findIndex(msg => msg.sequence === message.sequence);
          if (existingMessageIndex > -1) {
            // Replace the existing message with the updated one
            const newMessages = [...prevMessages];
            newMessages[existingMessageIndex] = message;
            return sortBy(newMessages, "sequence");
          }
          // If not found, append the new message
          return sortBy([...prevMessages, message], "sequence");
        });

        // If the message is completed, update the completedMessages state
        switch (message.status) {
          case "complete":
            setCompletedMessages(prevCompleted => {
              return [...prevCompleted, message];
            });
            setIsProcessing(false);
            break;

          case "streaming":
            setIsProcessing(true);
            break;

          case "error":
            setMessages([message]);
            setIsProcessing(false);
            switch (message.error) {
              case "must_reconnect":
                setIsProcessing(false);
                setSessionId(null);
                setSessionUrl(null);
                setSocket(null);
                break;
            }
        }
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    };

    socket.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    socket.onclose = () => {
      console.log("WebSocket connection closed");
    };
    setSocket(socket);

    // Cleanup function
    return () => {
      socket.close();
    };
  }, [sessionId, sessionUrl]);

  // Helper function to send requests via WebSocket
  const sendRequestToWebSocket = (socket, requestType, params) => {
    if (socket.readyState === WebSocket.OPEN) {
      setIsProcessing(true);
      socket.send(JSON.stringify({
        request_type: requestType,
        ...params
      }));
      return true;
    }
    return false;
  };

  // Method to send requests to the AI manager
  const sendAIRequest = useCallback((requestType: string, params: any) => {
    // If we don't have a session yet, start one and queue the request
    if (!sessionId) {
      // Create a new LLM session
      createLLMSession({ variables: { action: "session_create" } });

      // Queue the request to be sent when session is established
      requestQueueRef.current.push({ requestType, params });
      return;
    }

    if (socket && socket.readyState === WebSocket.CLOSED) {
      setSocket(null);
      setSessionId(null);
    }

    if (!socket || socket.readyState !== WebSocket.OPEN) {
      requestQueueRef.current.push({ requestType, params });
      return;
    }

    // Send the request directly
    setIsProcessing(true);
    sendRequestToWebSocket(socket, requestType, params);
  }, [sessionId, sessionUrl, createLLMSession, socket]);

  // Method to clear message history
  const clearMessages = useCallback(() => {
    setMessages([]);
    setIsProcessing(false);
  }, []);

  const totalClearMessages = useCallback(() => {
    setMessages([]);
    setCompletedMessages([]);
    setIsProcessing(false);
  }, []);

  const contextValue = useMemo(() => ({
    isProcessing,
    messages,
    completedMessages,
    sendAIRequest,
    clearMessages,
    totalClearMessages,
    hasAiPermission
  }), [isProcessing, messages, completedMessages, sendAIRequest, clearMessages, totalClearMessages, hasAiPermission]);

  return (
    <AIManagerContext.Provider value={contextValue}>
      {children}
    </AIManagerContext.Provider>
  );
};
